import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
	createCarouselItem,
	deleteCarouselItem,
	listCarousel,
	updateCarouselItem,
} from "../api/content";
import Modal from "../components/Modal";
import { capitalizeFirstLetter } from "../../utils/letters";
import { convertToWebp } from "../../utils/image";
import ConfirmModal from "../components/ConfirmModal";
import StatusBadge from "../components/StatusBadge";

/** Límite de elementos visibles en carrusel del home. */
const CAROUSEL_MAX_ITEMS = 5;
const CAROUSEL_ROWS_PER_PAGE_DEFAULT = 10;
const CAROUSEL_ALLOWED_ROWS = [10, 25, 50, 100];

const CAROUSEL_SORT_OPTIONS = [
	{ value: "orden", label: "Orden" },
	{ value: "titulo", label: "Título" },
	{ value: "activo", label: "Activo" },
];

const CAROUSEL_ALLOWED_SORTS = new Set(
	CAROUSEL_SORT_OPTIONS.map((option) => option.value),
);
const CAROUSEL_ALLOWED_DIRECTIONS = new Set(["asc", "desc"]);

function parsePositiveInt(rawValue, fallbackValue) {
	const parsed = Number(rawValue);
	if (!Number.isInteger(parsed) || parsed < 1) return fallbackValue;
	return parsed;
}

function parseRows(rawValue) {
	const parsed = Number(rawValue);
	if (!CAROUSEL_ALLOWED_ROWS.includes(parsed)) {
		return CAROUSEL_ROWS_PER_PAGE_DEFAULT;
	}
	return parsed;
}

function parseSort(rawValue) {
	if (!CAROUSEL_ALLOWED_SORTS.has(rawValue)) return "orden";
	return rawValue;
}

function parseDirection(rawValue) {
	if (!CAROUSEL_ALLOWED_DIRECTIONS.has(rawValue)) return "asc";
	return rawValue;
}

const emptyForm = {
	titulo: "",
	subtitulo: "",
	imagen_url: null,
	url_destino: "",
	activo: true,
};

const RESOURCE_NOT_FOUND_FALLBACK = `${process.env.PUBLIC_URL}/img/404_not_found.png`;

function getFirstApiMessage(value) {
	if (Array.isArray(value)) return value[0] ?? "";
	if (typeof value === "string") return value;
	return "";
}

function getApiFieldErrors(data) {
	const next = {};
	if (!data || typeof data !== "object") return next;

	const keys = ["titulo", "subtitulo", "imagen_url", "url_destino", "activo"];
	for (const key of keys) {
		const message = getFirstApiMessage(data[key]);
		if (message) next[key] = message;
	}

	let detail = "";
	if (typeof data.detail === "string") {
		detail = data.detail;
	} else if (Array.isArray(data.non_field_errors)) {
		detail = data.non_field_errors[0] ?? "";
	}

	if (detail && !next.titulo && /titulo|title/i.test(detail)) {
		next.titulo = detail;
	}
	return next;
}

function escapeRegExp(value) {
	return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function highlightSearchMatch(value, query) {
	const text = (value ?? "").toString();
	const needle = query.trim();
	if (!needle) return text;

	const matcher = new RegExp(`(${escapeRegExp(needle)})`, "ig");
	const parts = text.split(matcher);
	let cursor = 0;

	return parts.map((part) => {
		const key = `${cursor}-${part}`;
		cursor += part.length;
		if (part.toLowerCase() === needle.toLowerCase()) {
			return (
				<mark className="search-highlight" key={`hit-${key}`}>
					{part}
				</mark>
			);
		}
		return <span key={`txt-${key}`}>{part}</span>;
	});
}

export default function CarouselList() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [alert, setAlert] = useState(null);
	const [search, setSearch] = useState(() => searchParams.get("q") || "");
	const [rowsPerPage, setRowsPerPage] = useState(() =>
		parseRows(searchParams.get("rows")),
	);
	const [currentPage, setCurrentPage] = useState(() =>
		parsePositiveInt(searchParams.get("page"), 1),
	);
	const [sortField, setSortField] = useState(() =>
		parseSort(searchParams.get("sort")),
	);
	const [sortDirection, setSortDirection] = useState(() =>
		parseDirection(searchParams.get("dir")),
	);

	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [detailOpen, setDetailOpen] = useState(false);
	const [detailItem, setDetailItem] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [destType, setDestType] = useState("home");
	const [itemToDelete, setItemToDelete] = useState(null);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});
	const [imagePreviewSrc, setImagePreviewSrc] = useState(null);
	const [draggedItemId, setDraggedItemId] = useState(null);
	const [dragOverItemId, setDragOverItemId] = useState(null);
	const [reordering, setReordering] = useState(false);

	useEffect(() => {
		if (form.imagen_url instanceof File) {
			const url = URL.createObjectURL(form.imagen_url);
			setImagePreviewSrc(url);
			return () => URL.revokeObjectURL(url);
		}
		setImagePreviewSrc(editing?.imagen_url ?? null);
	}, [form.imagen_url, editing?.imagen_url]);

	async function refresh() {
		setLoading(true);
		try {
			const data = await listCarousel({});
			setItems(Array.isArray(data) ? data : (data.results ?? []));
		} catch {
			setAlert({ type: "danger", msg: "No se pudo cargar el carrusel." });
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		refresh();
	}, []);

	useEffect(() => {
		const nextSearch = searchParams.get("q") || "";
		const nextRows = parseRows(searchParams.get("rows"));
		const nextPage = parsePositiveInt(searchParams.get("page"), 1);
		const nextSort = parseSort(searchParams.get("sort"));
		const nextDirection = parseDirection(searchParams.get("dir"));

		setSearch((prev) => (prev === nextSearch ? prev : nextSearch));
		setRowsPerPage((prev) => (prev === nextRows ? prev : nextRows));
		setCurrentPage((prev) => (prev === nextPage ? prev : nextPage));
		setSortField((prev) => (prev === nextSort ? prev : nextSort));
		setSortDirection((prev) =>
			prev === nextDirection ? prev : nextDirection,
		);
	}, [searchParams]);

	const hasReachedMaxItems = items.length >= CAROUSEL_MAX_ITEMS;
	const canDragRows =
		!loading && !search.trim() && sortField === "orden" && sortDirection === "asc";

	function handleRowDragStart(event, itemId) {
		if (!canDragRows || reordering) {
			event.preventDefault();
			return;
		}

		setDraggedItemId(itemId);
		event.dataTransfer.effectAllowed = "move";
		event.dataTransfer.setData("text/carousel-item-id", String(itemId));
		event.dataTransfer.setData("text/plain", String(itemId));
	}

	function handleRowDragOver(event, itemId) {
		if (!canDragRows || reordering) return;
		event.preventDefault();
		setDragOverItemId(itemId);
	}

	function handleRowDragEnd() {
		setDraggedItemId(null);
		setDragOverItemId(null);
	}

	async function handleRowDrop(event, targetItemId) {
		if (!canDragRows || reordering) return;
		event.preventDefault();

		let sourceItemId = draggedItemId;
		if (!sourceItemId) {
			const raw =
				event.dataTransfer.getData("text/carousel-item-id") ||
				event.dataTransfer.getData("text/plain");
			sourceItemId = Number(raw);
		}

		const sourceIdNumber = Number(sourceItemId);
		const targetIdNumber = Number(targetItemId);

		if (!Number.isInteger(sourceIdNumber)) {
			handleRowDragEnd();
			return;
		}

		if (sourceIdNumber === targetIdNumber) {
			handleRowDragEnd();
			return;
		}

		const sourceItem = items.find((item) => item.id === sourceIdNumber);
		const targetItem = items.find((item) => item.id === targetIdNumber);

		if (!sourceItem || !targetItem) {
			handleRowDragEnd();
			return;
		}

		const orderedItems = [...items].sort(
			(left, right) => Number(left.orden ?? 0) - Number(right.orden ?? 0),
		);
		const sourceIndex = orderedItems.findIndex(
			(item) => item.id === sourceIdNumber,
		);
		const targetIndex = orderedItems.findIndex(
			(item) => item.id === targetIdNumber,
		);

		if (sourceIndex < 0 || targetIndex < 0) {
			handleRowDragEnd();
			return;
		}

		const swappedItems = [...orderedItems];
		[swappedItems[sourceIndex], swappedItems[targetIndex]] = [
			swappedItems[targetIndex],
			swappedItems[sourceIndex],
		];

		const nextOrderById = new Map(
			swappedItems.map((item, index) => [item.id, index + 1]),
		);

		const changedItems = swappedItems.filter(
			(item) => Number(item.orden ?? 0) !== nextOrderById.get(item.id),
		);

		if (!changedItems.length) {
			handleRowDragEnd();
			return;
		}

		setReordering(true);
		setAlert(null);

		try {
			await Promise.all(
				changedItems.map((item) => {
					const payload = new FormData();
					payload.append("orden", String(nextOrderById.get(item.id)));
					return updateCarouselItem(item.id, payload);
				}),
			);

			setItems((prev) =>
				prev.map((item) => {
					const nextOrden = nextOrderById.get(item.id);
					return Number.isInteger(nextOrden)
						? { ...item, orden: nextOrden }
						: item;
				}),
			);

			setAlert({
				type: "success",
				msg: "Orden de carrusel actualizado.",
			});
		} catch {
			setAlert({
				type: "danger",
				msg: "No se pudo actualizar el orden del carrusel.",
			});
		} finally {
			handleRowDragEnd();
			setReordering(false);
		}
	}

	function openCreate() {
		if (hasReachedMaxItems) {
			setAlert({
				type: "warning",
				msg: `Se alcanzó el máximo de ${CAROUSEL_MAX_ITEMS} elementos. Elimina uno para crear otro.`,
			});
			return;
		}

		setEditing(null);
		setForm({
			...emptyForm,
		});
		setAlert(null);
		setFieldErrors({});
		setDestType("home");
		setModalOpen(true);
	}

	function openEdit(item) {
		setEditing(item);
		setForm({
			titulo: item.titulo ?? "",
			subtitulo: item.subtitulo ?? "",
			imagen_url: null,
			url_destino: item.url_destino ?? "",
			activo: item.activo ?? true,
		});
		setAlert(null);
		setFieldErrors({});
		// Detectar tipo en base a url_destino (para no exponer rutas técnicas)
		const url = item.url_destino || "";
		if (url.startsWith("/#asesorias") || url.startsWith("/cursos"))
			setDestType("courses");
		else if (url.startsWith("/#noticias") || url.startsWith("/noticias"))
			setDestType("news");
		else if (url.startsWith("/#contacto") || url.startsWith("/contacto"))
			setDestType("contact");
		else if (url === "") setDestType("home");
		else setDestType("custom");
		setModalOpen(true);
	}

	function closeModal() {
		if (saving) return;
		setModalOpen(false);
		setFieldErrors({});
	}

	function openDetail(item) {
		setDetailItem(item);
		setDetailOpen(true);
	}

	function closeDetail() {
		setDetailOpen(false);
		setDetailItem(null);
	}

	function handleDetailImageError(event) {
		const target = event.currentTarget;
		if (!target) return;
		if (target.src?.includes("/img/404_not_found.png")) {
			target.onerror = null;
			return;
		}
		target.onerror = null;
		target.src = RESOURCE_NOT_FOUND_FALLBACK;
	}

	function validateForm() {
		const err = {};
		if (!(form.titulo ?? "").trim())
			err.titulo = "El título es obligatorio.";
		if (!editing && !form.imagen_url)
			err.imagen_url = "La imagen es obligatoria.";
		if (destType === "custom" && !(form.url_destino ?? "").trim())
			err.url_destino = "Indica la URL de destino.";
		setFieldErrors(err);
		return Object.keys(err).length === 0;
	}

	async function onSubmit(e) {
		e.preventDefault();
		if (!validateForm()) return;

		if (!editing && hasReachedMaxItems) {
			setAlert({
				type: "warning",
				msg: `No se pueden crear más de ${CAROUSEL_MAX_ITEMS} elementos en carrusel.`,
			});
			setModalOpen(false);
			return;
		}

		setSaving(true);
		setAlert(null);
		try {
			const formData = new FormData();
			const nextCreateOrder =
				Math.max(
					0,
					...items.map((item) => Number(item.orden ?? 0)),
				) + 1;
			formData.append("titulo", form.titulo);
			formData.append("subtitulo", form.subtitulo);
			formData.append("url_destino", form.url_destino);
			formData.append("activo", form.activo);
			if (!editing) {
				formData.append("orden", String(nextCreateOrder));
			}

			if (form.imagen_url instanceof File) {
				try {
					const webpFile = await convertToWebp(
						form.imagen_url,
						0.8,
					);
					formData.append("imagen_url", webpFile);
				} catch (error) {
					console.error(
						"No se pudo convertir a WebP, enviando original",
						error,
					);
					formData.append("imagen_url", form.imagen_url);
				}
			}

			if (editing) {
				await updateCarouselItem(editing.id, formData);
				setAlert({
					type: "success",
					msg: "Elemento actualizado correctamente.",
				});
			} else {
				await createCarouselItem(formData);
				setAlert({
					type: "success",
					msg: "Elemento creado correctamente.",
				});
			}
			setModalOpen(false);
			await refresh();
		} catch (e) {
			const apiData = e.response?.data || {};
			const apiFieldErrors = getApiFieldErrors(apiData);
			setFieldErrors((prev) => ({ ...prev, ...apiFieldErrors }));

			let fallbackMsg = "No se pudo guardar el elemento del carrusel.";
			if (typeof apiData?.detail === "string") {
				fallbackMsg = apiData.detail;
			} else if (Object.keys(apiFieldErrors).length === 0) {
				const unmapped = Object.entries(apiData)
					.map(([k, v]) => `${k}: ${getFirstApiMessage(v)}`)
					.join(" | ");
				if (unmapped) fallbackMsg = `Error del servidor: ${unmapped}`;
			}

			if (Object.keys(apiFieldErrors).length === 0) {
				setAlert({
					type: "danger",
					msg: fallbackMsg,
				});
			} else {
				setAlert(null);
			}
		} finally {
			setSaving(false);
		}
	}

	function openDeleteConfirm(item) {
		setItemToDelete(item);
	}

	async function confirmDelete() {
		if (!itemToDelete) return;
		setDeleteLoading(true);
		try {
			await deleteCarouselItem(itemToDelete.id);
			setAlert({
				type: "success",
				msg: "Elemento eliminado correctamente.",
			});
			setItemToDelete(null);
			await refresh();
		} catch {
			setAlert({
				type: "danger",
				msg: "No se pudo eliminar.",
			});
		} finally {
			setDeleteLoading(false);
		}
	}

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return items;
		return items.filter((x) => {
			const activeLabel = x.activo ? "si" : "no";
			return (
				(x.titulo ?? "").toLowerCase().includes(q) ||
				String(x.orden ?? "")
					.toLowerCase()
					.includes(q) ||
				activeLabel.includes(q)
			);
		});
	}, [items, search]);

	const sortedItems = useMemo(() => {
		const data = [...filtered];
		data.sort((left, right) => {
			if (sortField === "orden") {
				const leftValue = Number(left.orden ?? 0);
				const rightValue = Number(right.orden ?? 0);
				return sortDirection === "asc"
					? leftValue - rightValue
					: rightValue - leftValue;
			}

			if (sortField === "activo") {
				const leftValue = left.activo ? 1 : 0;
				const rightValue = right.activo ? 1 : 0;
				return sortDirection === "asc"
					? leftValue - rightValue
					: rightValue - leftValue;
			}

			const leftValue = (left[sortField] ?? "").toString().toLowerCase();
			const rightValue = (right[sortField] ?? "")
				.toString()
				.toLowerCase();
			if (leftValue === rightValue) return 0;
			if (sortDirection === "asc") return leftValue > rightValue ? 1 : -1;
			return leftValue < rightValue ? 1 : -1;
		});
		return data;
	}, [filtered, sortDirection, sortField]);

	useEffect(() => {
		setCurrentPage(1);
	}, [search, rowsPerPage, sortField, sortDirection]);

	const totalRows = sortedItems.length;
	const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

	useEffect(() => {
		if (currentPage > totalPages) setCurrentPage(totalPages);
	}, [currentPage, totalPages]);

	useEffect(() => {
		const nextParams = new URLSearchParams(searchParams);

		if (search.trim()) nextParams.set("q", search.trim());
		else nextParams.delete("q");

		if (rowsPerPage !== CAROUSEL_ROWS_PER_PAGE_DEFAULT) {
			nextParams.set("rows", String(rowsPerPage));
		} else {
			nextParams.delete("rows");
		}

		if (currentPage !== 1) nextParams.set("page", String(currentPage));
		else nextParams.delete("page");

		if (sortField !== "orden") nextParams.set("sort", sortField);
		else nextParams.delete("sort");

		if (sortDirection !== "asc") nextParams.set("dir", sortDirection);
		else nextParams.delete("dir");

		if (nextParams.toString() !== searchParams.toString()) {
			setSearchParams(nextParams, { replace: true });
		}
	}, [
		search,
		rowsPerPage,
		currentPage,
		sortField,
		sortDirection,
		searchParams,
		setSearchParams,
	]);

	function resetTableState() {
		setSearch("");
		setRowsPerPage(CAROUSEL_ROWS_PER_PAGE_DEFAULT);
		setCurrentPage(1);
		setSortField("orden");
		setSortDirection("asc");
		setSearchParams(new URLSearchParams(), { replace: true });
	}

	const startIndex = (currentPage - 1) * rowsPerPage;
	const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
	const visibleItems = sortedItems.slice(
		startIndex,
		startIndex + rowsPerPage,
	);

	let carouselRows;
	if (loading) {
		carouselRows = (
			<tr>
				<td colSpan="4" className="text-muted p-4">
					<span>Cargando...</span>
				</td>
			</tr>
		);
	} else if (visibleItems.length === 0) {
		carouselRows = (
			<tr>
				<td colSpan="4" className="text-muted p-4">
					<span>
						{search
							? "No se encontraron resultados para tu búsqueda."
							: "Sin elementos en el carrusel."}
					</span>
				</td>
			</tr>
		);
	} else {
		carouselRows = visibleItems.map((c) => (
			<tr
				key={c.id}
				className={`${dragOverItemId === c.id ? "carousel-row-drag-over" : ""} ${draggedItemId === c.id ? "carousel-row-dragging" : ""}`.trim()}
				draggable={canDragRows && !reordering}
				onDragStart={(event) => handleRowDragStart(event, c.id)}
				onDragOver={(event) => handleRowDragOver(event, c.id)}
				onDragEnter={(event) => handleRowDragOver(event, c.id)}
				onDrop={(event) => handleRowDrop(event, c.id)}
				onDragEnd={handleRowDragEnd}
			>
				<td>
					<span>
						<i
							className="ri-draggable me-2 text-muted carousel-row-drag-icon"
							aria-hidden
						/>
						<strong>{highlightSearchMatch(c.orden, search)}</strong>
					</span>
				</td>
				<td>
					<span>
						{highlightSearchMatch(
							capitalizeFirstLetter(c.titulo),
							search,
						)}
					</span>
				</td>
				<td>
					<StatusBadge
						active={Boolean(c.activo)}
						title="Elemento activo en carrusel"
					/>
				</td>
				<td>
					<div className="d-flex justify-content-center align-items-center gap-1 flex-nowrap">
						<button
							type="button"
							className="btn-sm adm-see-btn d-inline-flex align-items-center justify-content-center border-0 text-nowrap"
							onClick={() => openDetail(c)}
							title="Detalle"
							aria-label="Detalle"
						>
							<i className="ri-eye-fill" />
						</button>
						<button
							type="button"
							className="btn-sm adm-upd-btn d-inline-flex align-items-center justify-content-center border-0 text-nowrap"
							onClick={() => openEdit(c)}
							title="Editar"
							aria-label="Editar"
						>
							<i className="ri-edit-fill" />
						</button>
						<button
							type="button"
							className="btn-sm adm-del-btn d-inline-flex align-items-center justify-content-center border-0 text-nowrap"
							onClick={() => openDeleteConfirm(c)}
							title="Eliminar"
							aria-label="Eliminar"
						>
							<i className="ri-delete-bin-line" />
						</button>
					</div>
				</td>
			</tr>
		));
	}

	return (
		<div className="container-fluid">
			<div className="container-principal">
				<div>
					<h2 className="m-0">Carrusel</h2>
				</div>

				<div className="admin-actions-wrapper w-100">
					<button
						className="admin-btn"
						onClick={openCreate}
						disabled={hasReachedMaxItems}
						title={
							hasReachedMaxItems
								? `Límite alcanzado (${CAROUSEL_MAX_ITEMS}). Elimina un elemento para habilitar la creación.`
								: `Máximo ${CAROUSEL_MAX_ITEMS} elementos en el carrusel.`
						}
					>
						<span className="text-nowrap">Nuevo elemento</span>
					</button>
				</div>
			</div>

			{alert && (
				<div
					className={`alert alert-${alert.type} mt-3 mb-0`}
					role="alert"
				>
					{alert.msg}
				</div>
			)}

			<div className="card mt-3 shadow-sm">
				<div className="card-body p-0">
					{!canDragRows && (
						<div className="alert alert-info rounded-0 border-0 border-bottom mb-0 py-2 px-3" role="status">
							Para reordenar con arrastre, limpia la búsqueda y usa orden por "Orden" en modo ascendente.
						</div>
					)}
					<div className="table-toolbar table-toolbar-locked">
						<div className="toolbar-group toolbar-pagination-group">
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(1)}
								disabled={currentPage === 1}
							>
								&lt;&lt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() =>
									setCurrentPage((prev) =>
										Math.max(1, prev - 1),
									)
								}
								disabled={currentPage === 1}
							>
								&lt;
							</button>
							<select
								className="form-select toolbar-select toolbar-page-select"
								value={currentPage}
								onChange={(e) =>
									setCurrentPage(Number(e.target.value))
								}
								disabled={totalPages <= 1}
							>
								{Array.from(
									{ length: totalPages },
									(_, idx) => idx + 1,
								).map((page) => (
									<option key={page} value={page}>
										{page}
									</option>
								))}
							</select>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() =>
									setCurrentPage((prev) =>
										Math.min(totalPages, prev + 1),
									)
								}
								disabled={currentPage >= totalPages}
							>
								&gt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(totalPages)}
								disabled={currentPage >= totalPages}
							>
								&gt;&gt;
							</button>
						</div>

						<div className="search-group toolbar-search-group d-flex align-items-center gap-2">
							<input
								className="form-control"
								value={search} onChange={(e) => setSearch(e.target.value)}
								placeholder="Buscar por orden, titulo o activo"
								maxLength={120}
							/>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => {
									setSearch("");
									setCurrentPage(1);
								}}
								disabled={!search.trim()}
								title="Limpiar búsqueda"
								aria-label="Limpiar búsqueda"
							>
								<i className="ri-eraser-line" />
							</button>
						</div>

						<div className="toolbar-group toolbar-controls-group">
							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Número de filas:</span>
								<select
									className="form-select toolbar-select"
									value={rowsPerPage}
									onChange={(e) =>
										setRowsPerPage(Number(e.target.value))
									}
								>
									<option value={10}>10</option>
									<option value={25}>25</option>
									<option value={50}>50</option>
									<option value={100}>100</option>
								</select>
							</label>

							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Ordenar por:</span>
								<select
									className="form-select toolbar-select"
									value={sortField}
									onChange={(e) =>
										setSortField(e.target.value)
									}
								>
									{CAROUSEL_SORT_OPTIONS.map((option) => (
										<option
											key={option.value}
											value={option.value}
										>
											{option.label}
										</option>
									))}
								</select>
							</label>

							<button
								type="button"
								className="toolbar-order-btn"
								onClick={() =>
									setSortDirection((prev) =>
										prev === "asc" ? "desc" : "asc",
									)
								}
							>
								{sortDirection === "asc" ? "Asc" : "Desc"}
							</button>

							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={resetTableState}
								title="Reiniciar tabla"
							>
								Reiniciar
							</button>
						</div>
					</div>

					<div className="table-responsive table-wrapper-scroll">
						<table className="table table-sm table-hover mb-0 align-middle table-organizations-nowrap">
							<thead className="table-light">
								<tr>
									<th style={{ width: 80 }}>
										<span>Orden</span>
									</th>
									<th>
										<span>Título</span>
									</th>
									<th>
										<span>Activo</span>
									</th>
									<th
										style={{ width: 220 }}
										className="text-center"
									>
										<span>Acciones</span>
									</th>
								</tr>
							</thead>
							<tbody>{carouselRows}</tbody>
						</table>
					</div>
					<div className="table-footer-summary px-3 py-2 border-top">
						<span>
							Mostrando {totalRows === 0 ? 0 : startIndex + 1} -{" "}
							{endIndex} de {totalRows} registros
						</span>
					</div>
				</div>
			</div>
			<Modal
				title="Detalle de elemento del carrusel"
				open={detailOpen}
				onClose={closeDetail}
				dialogClassName="modal-dialog modal-xl"
				footer={
					<button className="adm-save-btn" onClick={closeDetail}>
						<span>Cerrar</span>
					</button>
				}
			>
				{detailItem ? (
					<>
						<section className="img-carousel mb-0">
							<div className="carousel-item active">
								<img
									src={
										detailItem.imagen_url ||
										"/img/carousel/mujer.webp"
									}
									alt={detailItem.titulo || "Elemento de carrusel"}
									onError={handleDetailImageError}
								/>
								<div className="hero-overlay">
									<div className="inf-carousel">
										<span className="txt_one">
											{capitalizeFirstLetter(
												detailItem.titulo || "",
											)}
										</span>
										<div className="inf-carousel-btns">
											<span className="txt-button">
												<span>Ver más</span>
											</span>
											<span className="txt_two">
												{capitalizeFirstLetter(
													detailItem.subtitulo || "",
												)}
											</span>
										</div>
									</div>
								</div>
							</div>
						</section>
						<div className="mt-3">
							<span className="text-muted fs-6">
								Destino configurado: {detailItem.url_destino || "-"}
							</span>
						</div>
					</>
				) : (
					<span>No hay información para mostrar.</span>
				)}
			</Modal>
			<Modal
				title={
					editing
						? "Editar elemento de carrusel"
						: "Nuevo elemento de carrusel"
				}
				open={modalOpen}
				onClose={closeModal}
				footer={
					<>
						<button
							className="adm-del-btn"
							onClick={closeModal}
							disabled={saving}
						>
							<span>Cancelar</span>
						</button>
						<button
							className="adm-save-btn"
							onClick={onSubmit}
							disabled={saving}
						>
							<span>{saving ? "Guardando..." : "Guardar"}</span>
						</button>
					</>
				}
			>
				{alert && (
					<div className={`alert alert-${alert.type}`} role="alert">
						{alert.msg}
					</div>
				)}

				<form onSubmit={onSubmit} className="row g-3">
					<div className="col-12">
						<label className="form-label" htmlFor="carousel-title">
							<span>Título</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<input
							id="carousel-title"
							className={`form-control ${fieldErrors.titulo ? "is-invalid" : ""}`}
							value={form.titulo}
							placeholder="Ej: Programas para organizaciones"
							maxLength={30}
							onChange={(e) => {
								setForm((p) => ({
									...p,
									titulo: e.target.value,
								}));
								if (fieldErrors.titulo)
									setFieldErrors((p) => ({
										...p,
										titulo: undefined,
									}));
							}}
							aria-invalid={!!fieldErrors.titulo}
						/>
						<span className="text-muted fs-6">
							Max. 30 caracteres
						</span>
						{fieldErrors.titulo && (
							<div className="invalid-feedback">
								{fieldErrors.titulo}
							</div>
						)}
					</div>

					<div className="col-12">
						<label
							className="form-label"
							htmlFor="carousel-subtitle"
						>
							<span>Subtítulo </span>
						</label>
						<input
							id="carousel-subtitle"
							className="form-control"
							value={form.subtitulo}
							maxLength={30} onChange={(e) => setForm((p) => ({
								...p,
								subtitulo: e.target.value,
							}))
							}
							placeholder="Ej: Aprende a tu propio ritmo"
						/>
						<span className="text-muted fs-6">
							Max. 30 caracteres
						</span>
					</div>

					<div className="col-12">
						<label
							className="form-label"
							htmlFor="carousel-image-url"
						>
							<span>Imagen del carrusel</span>
							{!editing && <span className="text-danger">*</span>}
						</label>
						<input
							id="carousel-image-url"
							type="file"
							accept="image/*"
							className={`form-control ${fieldErrors.imagen_url ? "is-invalid" : ""}`}
							onChange={(e) => {
								const file = e.target.files?.[0] || null;
								setForm((p) => ({
									...p,
									imagen_url: file,
								}));
								if (fieldErrors.imagen_url)
									setFieldErrors((p) => ({
										...p,
										imagen_url: undefined,
									}));
							}}
							aria-invalid={!!fieldErrors.imagen_url}
						/>
						{fieldErrors.imagen_url && (
							<div className="invalid-feedback d-block">
								{fieldErrors.imagen_url}
							</div>
						)}
						{imagePreviewSrc && (
							<div className="mt-2">
								<small className="text-muted d-block mb-1">
									<span>Vista previa:</span>
								</small>
								<img
									src={imagePreviewSrc}
									alt="Preview"
									style={{
										maxWidth: "100%",
										maxHeight: 160,
										objectFit: "contain",
										backgroundColor: "transparent",
										borderRadius: 8,
									}}
								/>
							</div>
						)}
					</div>

					<div className="col-12">
						<label
							className="form-label"
							htmlFor="carousel-destination-type"
						>
							<span>
								¿A dónde quieres llevar a la persona al hacer
								clic?
							</span>
						</label>
						<select
							id="carousel-destination-type"
							className="form-select mb-2"
							value={destType}
							onChange={(e) => {
								const value = e.target.value;
								setDestType(value);
								// Sugerimos rutas internas conocidas para que el usuario no tenga que escribirlas
								if (value === "home") {
									setForm((p) => ({
										...p,
										url_destino: "/",
									}));
								} else if (value === "courses") {
									setForm((p) => ({
										...p,
										url_destino: "/cursos",
									}));
								} else if (value === "news") {
									setForm((p) => ({
										...p,
										url_destino: "/noticias",
									}));
								} else if (value === "contact") {
									setForm((p) => ({
										...p,
										url_destino: "/#contacto",
									}));
								}
							}}
						>
							<option value="home">
								Página principal
							</option>
							<option value="courses">
								Sección de cursos
							</option>
							<option value="news">
								Sección de noticias
							</option>
							<option value="contact">
								Sección de contacto
							</option>
							<option value="custom">
								Otro lugar (configuración avanzada)
							</option>
						</select>
						{destType === "custom" && (
							<>
								<input
									className={`form-control ${fieldErrors.url_destino ? "is-invalid" : ""}`}
									value={form.url_destino} onChange={(e) => {
										setForm((p) => ({
											...p,
											url_destino: e.target.value,
										}));
										if (fieldErrors.url_destino)
											setFieldErrors((p) => ({
												...p,
												url_destino: undefined,
											}));
									}}
									placeholder="Ej: https://tusitio.cl/otra-pagina"
									aria-invalid={!!fieldErrors.url_destino}
								/>
								{fieldErrors.url_destino && (
									<div className="invalid-feedback">
										{fieldErrors.url_destino}
									</div>
								)}
								<span className="text-muted fs-6">
									Solo si necesitas llevar a una página
									especial. Si no, elige una de las opciones
									de arriba.
								</span>
							</>
						)}
					</div>

					<div className="col-12 d-flex align-items-center">
						<div className="form-check">
							<input
								className="form-check-input"
								type="checkbox"
								checked={form.activo}
								onChange={(e) =>
									setForm((p) => ({
										...p,
										activo: e.target.checked,
									}))
								}
								id="carouselActive"
							/>
							<label
								className="form-check-label"
								htmlFor="carouselActive"
							>
								<span>Activo</span>
							</label>
						</div>
					</div>

					<button type="submit" className="d-none">
						<span>enviar</span>
					</button>
				</form>
			</Modal>

			<ConfirmModal
				open={!!itemToDelete}
				onClose={() => !deleteLoading && setItemToDelete(null)}
				onConfirm={confirmDelete}
				title="Eliminar elemento del carrusel"
				message={
					itemToDelete
						? `¿Eliminar el elemento "${itemToDelete.titulo || itemToDelete.subtitulo || "sin título"}"? Esta acción no se puede deshacer.`
						: ""
				}
				confirmLabel="Eliminar"
				variant="danger"
				loading={deleteLoading}
			/>
		</div>
	);
}
